/**
 * HTTP请求工具
 * 基于axios封装，统一处理域名、请求头、错误等
 */
import axios from 'axios'
import config from '@/config'
import Vue from 'vue'

// 创建axios实例
const service = axios.create({
  baseURL: config.baseURL, // 基础URL
  timeout: config.timeout || 10000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json'
  }
})

/**
 * 请求拦截器 - 在发送请求前处理
 */
service.interceptors.request.use(
  config => {
    // 打印请求信息
    const fullUrl = (config.baseURL || '') + config.url
    const method = config.method?.toUpperCase() || 'GET'
    const params = config.params || config.data || {}
    // 添加token等认证信息（如需要可取消注释）
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')
    if (token) {
      config.headers['authorization'] = token
    }
    
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

/**
 * 响应拦截器 - 处理响应数据
 */
service.interceptors.response.use(
  response => {
    const res = response.data
    
    // 根据后端返回的数据结构处理
    // 如果后端返回格式为 { code: 200, data: {}, message: '' }
    if (res.code !== undefined) {
      if (res.code === 200 || res.code === 0) {
		   console.log(res)
        // 返回完整响应对象，包含data属性
        return Promise.resolve({
          ...res,
          data: res.data !== undefined ? res.data : res
        })
      } else {
		  console.log(res)
        // 业务错误处理
        const error = new Error(res.message || '请求失败')
        error.code = res.code
        Vue.prototype.$message.error(res.message || '请求失败')
        return Promise.reject(error)
      }
    }
    
    // 如果后端直接返回数据，包装成统一格式
    return Promise.resolve({
      code: 200,
      data: res,
      message: 'success'
    })
  },
  error => {
    
    let errorMessage = '请求失败'
    
    if (error.response) {
      // 服务器返回了错误状态码
      const { status, statusText, data } = error.response
     
      errorMessage = data?.message || `请求失败 (${status} ${statusText})`

    } else if (error.request) {
      // 请求已发出，但没有收到响应（通常是CORS或网络问题）
      errorMessage = '网络连接失败，请检查网络设置或CORS配置'
    } else {
      // 发送请求时出了点问题
     
      errorMessage = error.message || '请求失败'
    }
    
    // 统一错误提示
    if (error.message !== '请求失败') {
      Vue.prototype.$message.error(errorMessage)
    }
    return Promise.reject(error)
  }
)

/**
 * 通用请求方法
 * @param {string} url - 请求地址（相对路径）
 * @param {object} data - 请求参数（GET请求时为params，POST等为data）
 * @param {object} config - axios配置选项
 * @param {string} method - 请求方法 GET/POST/PUT/DELETE
 */
export function $request(url, data = {}, config = {}, method = 'GET') {
  // 统一处理URL，确保以/开头（如果baseURL为空或相对路径）
  // 如果URL已经是完整地址（http/https开头），则不处理
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    // 确保URL以/开头
    if (!url.startsWith('/')) {
      url = '/' + url
    }
  }
  
  const requestConfig = {
    url,
    method: method.toUpperCase(),
    ...config
  }
  
  // 根据请求方法设置参数
  if (method.toUpperCase() === 'GET' || method.toUpperCase() === 'DELETE') {
    requestConfig.params = data
  } else {
    requestConfig.data = data
  }
  
  return service(requestConfig)
}

/**
 * GET请求
 */
export function get(url, params = {}, options = {}) {
  return service({
    url,
    method: 'GET',
    params,
    ...options
  })
}

/**
 * POST请求
 */
export function post(url, data = {}, options = {}) {
  return service({
    url,
    method: 'POST',
    data,
    ...options
  })
}

/**
 * PUT请求
 */
export function put(url, data = {}, options = {}) {
  return service({
    url,
    method: 'PUT',
    data,
    ...options
  })
}

/**
 * DELETE请求
 */
export function del(url, params = {}, options = {}) {
  return service({
    url,
    method: 'DELETE',
    params,
    ...options
  })
}

// 导出axios实例和请求方法
export default service
export { service }


